import React, {
	createContext,
	useContext,
} from 'react';
import { useQuery } from 'react-query';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { Port } from '@api/utils/ports';
import type { GetVesselsResponse } from '@api/features/vessels/getVessels';
import type { ConsumptionSetProps } from '@api/models/consumption-set';
import type { EstimateCostProps } from '@api/models/estimate-cost';
import type { EstimateProps } from '@api/models/estimate';
import { getEstimateCost } from '@client/lib/api';
import { ChartererOption, useMiscHandlers } from './hooks/useMiscHandlers';
import {
	OnRefreshEuaPriceMutator,
	OnUpdateEstimate,
	useEstimateHandlers,
} from './hooks/useEstimateHandlers';
import { useCentralStore } from './CentralStoreContext';
import { useMiscEstimateCostHandler } from './hooks/useMiscEstimateCostHandler';

export type EstimatorContextType = {
	estimate: GetEstimateDetailsResponse | null;
	estimateResult: GetEstimateDetailsResponse | null | undefined;
	vessel?: GetVesselDetailsResponse | null;
	consumptionSets: Array<ConsumptionSetProps>;
	refreshVessel: () => void;
	onUpdateEstimate: OnUpdateEstimate;
	estimateLoading: boolean;
	charterers: GetCounterpartiesResponse | undefined;
	chartererOptions: ChartererOption[];
	ports: Port[] | undefined;
	portOptions: {
		label: string;
		value: number;
	}[];
	onRefreshEuaPriceMutator: OnRefreshEuaPriceMutator;
	allVessels: GetVesselsResponse | undefined;
	refreshAllVessels: () => void;
	fixtureHasInvoices: boolean | undefined;
	fixtureHasDisSyncedDAs: boolean | undefined;
	estimateCosts: EstimateCostProps[] | undefined;
	refreshEstimateCosts: () => Promise<any>;
	handleMiscCostChange: (value: number) => void;
	pendingChanges: Partial<EstimateProps>;
	setPendingChanges: (changes: Partial<EstimateProps>) => void;
	refreshEverything: () => void;
};

export const EstimatorContext = createContext<EstimatorContextType>(undefined!);
export const useEstimate = () => useContext(EstimatorContext);

const EstimateProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const {
		estimate: centralEstimate,
		selectedEstimateId,
		setSelectedEstimateId,
		setEstimateGroupId,
		syncEstimate,
		setSyncEstimate,
		vessel,
		refreshVessel,
		refreshEverything,
		setLoading,
	} = useCentralStore();

	const {
		estimate,
		estimateLoading,
		onRefreshEuaPriceMutator,
		onUpdateEstimate,
		allVessels,
		refreshAllVessels,
		fixtureHasInvoices,
		fixtureHasDisSyncedDAs,
		pendingChanges,
		setPendingChanges,
	} = useEstimateHandlers({
		selectedEstimateId,
		setSelectedEstimateId,
		setEstimateGroupId,
		syncEstimate,
		setSyncEstimate,
		remoteEstimate: centralEstimate,
		refreshEverything,
		setLoading,
	});

	const {
		ports,
		portOptions,
		charterers,
		chartererOptions,
	} = useMiscHandlers();

	const {
		data: estimateCosts,
		refetch: refreshEstimateCosts,
	} = useQuery(
		['estimateCosts', estimate?.id],
		async () => getEstimateCost(estimate?.id as number),
		{ enabled: !!estimate?.id },
	);

	const {
		handleMiscCostChange,
	} = useMiscEstimateCostHandler({
		estimate,
		estimateCosts,
		refreshEstimateCosts,
		onUpdateEstimate,
	});

	return (
		<EstimatorContext.Provider
			value={{
				estimate,
				estimateResult: centralEstimate,
				vessel,
				consumptionSets: vessel?.consumptionSets ?? [],
				refreshVessel,
				onUpdateEstimate,
				estimateLoading,
				charterers,
				chartererOptions,
				ports,
				portOptions,
				onRefreshEuaPriceMutator,
				refreshAllVessels,
				allVessels,
				fixtureHasInvoices,
				fixtureHasDisSyncedDAs,
				estimateCosts,
				refreshEstimateCosts,
				handleMiscCostChange,
				pendingChanges,
				setPendingChanges,
				refreshEverything,
			}}
		>
			{children}
		</EstimatorContext.Provider>
	);
};

export default EstimateProvider;
